import React, { useState } from "react";
import { Box, Typography, Link, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import AuthenticationTextInput from "../../../components/common/TextInput/AuthenticationTextInput";
import PrimaryButton from "../../../components/common/PrimaryButton";
import Heading from "../../../components/common/AutenticationTitles/Heading";
import Subtitle from "../../../components/common/AutenticationTitles/Subtitle";
import AuthenticationLeftContent from "../../../components/common/AutenticationLeftContent";
import { registerUserAction, loginUserAction, fetchUserDataAction } from "../../../store/slices/authSlice";
import { validateIndianMobile } from "../../../utils/phoneValidation";

const RegisterPage = ({ onClose, onViewChange, isMobile = false }) => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name) newErrors.name = "Name is required";
    const phoneCheck = validateIndianMobile(form.phone);
    if (!phoneCheck.isValid) newErrors.phone = phoneCheck.error;
    if (!form.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Invalid email";
    if (!form.password) newErrors.password = "Password is required";
    if (!form.confirmPassword)
      newErrors.confirmPassword = "Confirm Password is required";
    else if (form.password !== form.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        // Map form data to API payload structure
        const payload = {
          name: form.name,
          email: form.email,
          mobile: validateIndianMobile(form.phone).digits,
          password: form.password,
          confirm_password: form.confirmPassword, // Map confirmPassword to confirm_password
        };

        const result = await dispatch(registerUserAction(payload));
        if (registerUserAction.fulfilled.match(result)) {
          const regPayload = result.payload || {};
          const token = regPayload.accessToken || regPayload.token || null;

          if (!token) {
            // Auto-login using the same credentials
            const loginRes = await dispatch(loginUserAction({ email: form.email, password: form.password }));
            if (loginUserAction.fulfilled.match(loginRes)) {
              // Fetch user profile data after successful login
              dispatch(fetchUserDataAction());
            }
          } else {
            // If token received from register, fetch profile
            dispatch(fetchUserDataAction());
          }
          
          // New account from a protected link (Profile, 80G form...): go there, like login does.
          const from = location?.state?.from;
          const returnTo = from ? `${from.pathname || ""}${from.search || ""}` : null;
          if (returnTo) {
            navigate(returnTo, { replace: true });
          } else {
            // Close the dialog or navigate
            if (onClose) {
              onClose();
            } else if (isMobile) {
              navigate("/");
            }
            if (!isMobile && onViewChange) {
              onViewChange("verify");
            }
          }
        }
      } catch (error) {
        console.error("Registration failed:", error);
      }
    }
  };

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : isTablet ? "column" : "row",
        backgroundColor: "#FFFFFF",
        borderRadius: isMobile ? "12px" : isTablet ? "12px" : "22px",
        position: "relative",
        padding: isMobile ? "0px" : isTablet ? "0px" : "30px 0px 30px 30px",
        height: isMobile ? "auto" : isTablet ? "100vh" : "auto",
        overflow: isMobile ? "hidden" : isTablet ? "hidden" : "visible",
      }}
    >
      <Box
        className="left-width"
        flex={1}
        sx={{
          maxWidth: isMobile
            ? "100%"
            : isTablet
            ? "100%"
            : isLaptop
            ? "300px"
            : "499px",
          "& .authentication-left-content": {
            height: isMobile ? "40vh" : undefined,
            minHeight: isMobile ? "200px" : undefined,
            maxHeight: isMobile ? "40vh" : undefined,
          },
        }}
      >
        <AuthenticationLeftContent onClose={onClose} />
      </Box>
      <Box
        className="right-content"
        flex={1}
        p={isMobile ? 2 : isTablet ? 2 : 4}
        sx={{
          maxWidth: isMobile ? "100%" : isLaptop ? "390px" : "100%",
          marginInline: isMobile ? "0" : isTablet ? "0" : "auto",
          height: isMobile ? "50vh" : isTablet ? "60vh" : "auto",
          overflow: isMobile ? "auto" : isTablet ? "auto" : "visible",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: isMobile ? "100%" : isTablet ? "100%" : "auto",
          position: "relative",
          borderRadius: isMobile
            ? "0 0 12px 12px"
            : isTablet
            ? "0 0 12px 12px"
            : "0",
        }}
      >
        <Heading>Register your account</Heading>
        <Subtitle>Enter your basic details below</Subtitle>
        <form
          onSubmit={handleSubmit}
          style={{ marginTop: isMobile ? "0px" : isTablet ? "0px" : "0px" }}
        >
          {/* Mobile: Email first, then Name and Phone */}
          {isMobile ? (
            <>
              <AuthenticationTextInput
                label="Email Address"
                type="email"
                value={form.email}
                placeholder="Enter your Email"
                onChange={handleChange("email")}
                error={errors.email}
                helperText={errors.email}
                required
              />
              <Box display="flex" gap={1}>
                <Box flex={1}>
                  <AuthenticationTextInput
                    label="Name"
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder="Enter your Name"
                    error={errors.name}
                    helperText={errors.name}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <AuthenticationTextInput
                    label="Phone Number"
                    placeholder="Enter Phone Number"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    phoneInput
                    error={errors.phone}
                    helperText={errors.phone}
                    required
                  />
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Desktop/Tablet: Name and Phone first, then Email */}
              <Box display="flex" gap={isTablet ? 1 : 2}>
                <Box flex={1}>
                  <AuthenticationTextInput
                    label="Name"
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder="Enter your Name"
                    error={errors.name}
                    helperText={errors.name}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <AuthenticationTextInput
                    label="Phone Number"
                    placeholder="Enter Phone Number"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    phoneInput
                    error={errors.phone}
                    helperText={errors.phone}
                    required
                  />
                </Box>
              </Box>
              <AuthenticationTextInput
                label="Email Address"
                type="email"
                value={form.email}
                placeholder="Enter your Email"
                onChange={handleChange("email")}
                error={errors.email}
                helperText={errors.email}
                required
              />
            </>
          )}
          <Box
            display="flex"
            gap={isMobile ? 1 : isTablet ? 1.5 : 2}
            mb={isMobile ? 1 : isTablet ? 1 : 2}
          >
            <Box flex={1}>
              <AuthenticationTextInput
                label="Password"
                type="password"
                placeholder="Enter your Password"
                value={form.password}
                onChange={handleChange("password")}
                error={errors.password}
                helperText={errors.password}
                required
              />
            </Box>
            <Box flex={1}>
              <AuthenticationTextInput
                label="Confirm Password"
                type="password"
                placeholder="Enter your Password"
                value={form.confirmPassword}
                onChange={handleChange("confirmPassword")}
                error={errors.confirmPassword}
                helperText={errors.confirmPassword}
                required
              />
            </Box>
          </Box>
          <Box sx={{ mt: isMobile ? 1 : isTablet ? 0 : 2 }}>
            <PrimaryButton disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </PrimaryButton>
          </Box>
        </form>
        {error && (
          <Typography 
            align="center" 
            mt={2} 
            style={{ 
              fontSize: "14px", 
              fontWeight: "500", 
              color: "#F04951" 
            }}
          >
            {error}
          </Typography>
        )}
        <Typography
          align="center"
          mt={isMobile ? 2 : isTablet ? 1 : 3}
          style={{
            fontSize: isMobile ? "14px" : isTablet ? "14px" : "16px",
            fontWeight: "500",
            color: "#4A4A49",
          }}
        >
          Already have an account?{" "}
          <Link
            href="#"
            underline="none"
            onClick={(e) => {
              e.preventDefault();
              if (onViewChange) onViewChange("login");
            }}
            style={{
              fontSize: isMobile ? "14px" : isTablet ? "14px" : "16px",
              fontWeight: "700",
              color: "#141414",
              cursor: "pointer",
            }}
          >
            Login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterPage;
